import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import Header from './Header.jsx'
import Footer from './Footer.jsx'
import AdUnit from './AdUnit.jsx'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/20/solid'
import { supabase } from '../lib/supabase'
import { MIN_YEAR, MAX_YEAR } from './constants.js'
import DocumentMeta from './DocumentMeta.jsx'
import { trackEvent } from './lib/analytics'
import { reportError } from './lib/reportError'

const GREEK_UNI_CITIES = [
  'Αθήνα','Θεσσαλονίκη','Πάτρα','Ιωάννινα','Ηράκλειο','Χανιά','Ρέθυμνο',
  'Λάρισα','Βόλος','Τρίκαλα','Καρδίτσα','Λαμία','Άμφισσα','Κοζάνη','Καστοριά',
  'Φλώρινα','Πτολεμαΐδα','Σέρρες','Καβάλα','Δράμα','Αλεξανδρούπολη','Κομοτηνή',
  'Ξάνθη','Ορεστιάδα','Μυτιλήνη','Χίος','Σάμος','Ρόδος','Σύρος','Ναύπλιο',
  'Τρίπολη','Καλαμάτα','Σπάρτη','Αγρίνιο','Μεσολόγγι','Άρτα','Πρέβεζα',
  'Ζάκυνθος','Κεφαλονιά',
]

const PAGE_SIZE = 15

// ✅ Persist across React 18 StrictMode "fake remount" (dev only)
const initialFetchKeys = new Set()

function buildScoresQuery({ filters, ordering, page }) {
  const q = new URLSearchParams()
  if (filters.department) q.set('department', filters.department)
  if (filters.city) q.set('city', filters.city)
  if (filters.field) q.set('field', filters.field)
  if (filters.scoreMin) q.set('scoreMin', filters.scoreMin)
  if (filters.scoreMax) q.set('scoreMax', filters.scoreMax)
  if (ordering) q.set('ordering', ordering)
  if (page && page !== 1) q.set('page', String(page))
  return q.toString()
}

function parseScoresQuery(search) {
  const q = new URLSearchParams(search || '')
  return {
    department: q.get('department') || '',
    city: q.get('city') || '',
    field: q.get('field') || '',
    scoreMin: q.get('scoreMin') || '',
    scoreMax: q.get('scoreMax') || '',
    ordering: q.get('ordering') || null,
    page: Number(q.get('page') || '1') || 1,
  }
}


function ScoresPage() {
  const { year } = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  const selectedYear =
    year && !isNaN(year)
      ? Math.min(Math.max(Number(year), MIN_YEAR), MAX_YEAR)
      : MAX_YEAR

  const [filters, setFilters] = useState({
    department: '',
    city: '',
    field: '',
    scoreMin: '',
    scoreMax: '',
    year: selectedYear.toString(),
  })
  const [displayYear, setDisplayYear] = useState(selectedYear)
  const [ordering, setOrdering] = useState('-score')

  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [hasNext, setHasNext] = useState(false)
  const [hasPrev, setHasPrev] = useState(false)
  const [totalResults, setTotalResults] = useState(0)
  const [fields, setFields] = useState([])

  // Mobile filter sheet
  const [showFilters, setShowFilters] = useState(false)

  const lastPage = Math.max(1, Math.ceil(totalResults / PAGE_SIZE))
  const sortedCities = [...GREEK_UNI_CITIES].sort((a, b) =>
    a.localeCompare(b, 'el', { sensitivity: 'base' })
  )

  // keep latest request to avoid race conditions (optional but useful)
  const requestIdRef = useRef(0)

  // Fetch fields dynamically
  useEffect(() => {
    const fetchFields = async () => {
      const { data, error } = await supabase
        .from('fields')
        .select('id, number, name')
        .order('number')

      if (error) {
        console.error('Error fetching fields', error)
        reportError(error, { where: 'ScoresPage.fetchFields' })
        return
      }

      setFields(data || [])
    }

    fetchFields()
  }, [])

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      year: selectedYear.toString(),
    }))
  }, [selectedYear])

  useEffect(() => {
    const parsed = parseScoresQuery(location.search)
  
    // apply query -> state
    const nextFilters = {
      ...filters,
      year: selectedYear.toString(),
      department: parsed.department,
      city: parsed.city,
      field: parsed.field,
      scoreMin: parsed.scoreMin,
      scoreMax: parsed.scoreMax,
    }
  
    if (parsed.ordering) setOrdering(parsed.ordering)
    setFilters(nextFilters)
  
    // auto-search using page from query
    handleSubmit(null, parsed.page, nextFilters, parsed.ordering || ordering)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, location.pathname])  

  useEffect(() => {
    if (!year) return

    const numericYear = Number(year)
    if (Number.isNaN(numericYear) || numericYear < MIN_YEAR || numericYear > MAX_YEAR) {
      navigate(`/vaseis/${MAX_YEAR}?ordering=-score`, { replace: true })
    }
  }, [year, navigate])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFilters((prev) => ({ ...prev, [name]: value }))
  }  

  const handleSubmit = async (e, newPage = 1, filtersOverride = null, orderingOverride = null) => {
    if (e) e.preventDefault()

    const activeFilters = filtersOverride || filters
    const activeOrdering = orderingOverride || ordering

    if (e) {
      trackEvent('scores_search_submitted', {
        page: newPage,
        ordering: activeOrdering,
    
        // Values (so you can analyze what users actually choose)
        year: activeFilters.year || '',
        city: activeFilters.city || '',
        field: activeFilters.field || '',
        department: (activeFilters.department || '').trim(),
        score_min: activeFilters.scoreMin || '',
        score_max: activeFilters.scoreMax || '',
    
      })
    }    
    
    
    setLoading(true)
    setPage(newPage)
    if (newPage !== page) {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    }

    const myReqId = ++requestIdRef.current

    // 1) Department search -> ids
    let deptIds = null
    if (activeFilters.department) {
      const searchText = activeFilters.department.trim()
      const { data, error } = await supabase.rpc('search_departments', { search_text: searchText })
      if (error) {
        console.error(error)
        reportError(error, {
          where: 'scores_search_departments_rpc',
          search_text: searchText,
        })
      }      
      deptIds = data?.map((d) => d.id) || []
    }

    if (activeFilters.department && (deptIds?.length || 0) === 0) {
      if (myReqId !== requestIdRef.current) return
      setResults([])
      setTotalResults(0)
      setHasNext(false)
      setHasPrev(false)
      setLoading(false)
      setDisplayYear(Number(activeFilters.year))
      trackEvent('scores_no_results', {
        year: activeFilters.year,
        ordering: activeOrdering,
        page: newPage,
      })
      navigate(`/vaseis/${activeFilters.year}`, { replace: true })
      return
    }

    // 2) If field filter active: get paginated score IDs from SCORES (safe ordering + filtering)
    let pagedScoreIds = null
    let totalCount = null

    if (activeFilters.field) {
      let idQuery = supabase
        .from('scores')
        .select(
          `id,
           departments!inner(id, city),
           score_fields!inner(field_id)`,
          { count: 'exact' }
        )
        .eq('score_fields.field_id', activeFilters.field)
        .order('score', { ascending: activeOrdering === 'score' })
        .range((newPage - 1) * PAGE_SIZE, newPage * PAGE_SIZE - 1)

      // Apply the same filters on SCORES
      if (activeFilters.year) idQuery = idQuery.eq('year', Number(activeFilters.year))
      if (activeFilters.city) idQuery = idQuery.eq('departments.city', activeFilters.city)
      if (activeFilters.scoreMin) idQuery = idQuery.gte('score', Number(activeFilters.scoreMin))
      if (activeFilters.scoreMax) idQuery = idQuery.lte('score', Number(activeFilters.scoreMax))
      if (deptIds?.length) idQuery = idQuery.in('departments.id', deptIds)

      const { data: idData, count: idCount, error: idErr } = await idQuery

      if (myReqId !== requestIdRef.current) return

      if (idErr) {
        console.error(idErr)
        reportError(idErr, {
          where: 'scores_field_mode_id_query',
          filters: activeFilters,
          ordering: activeOrdering,
          page: newPage,
        })
        setResults([])
        setTotalResults(0)
        setHasNext(false)
        setHasPrev(false)
        setLoading(false)
        trackEvent('scores_no_results', {
          year: activeFilters.year,
          ordering: activeOrdering,
          page: newPage,
        })
        return
      }

      pagedScoreIds = (idData || []).map((r) => r.id)
      totalCount = idCount || 0

      if (pagedScoreIds.length === 0) {
        setResults([])
        setTotalResults(0)
        setHasNext(false)
        setHasPrev(false)
        setLoading(false)
        setDisplayYear(Number(activeFilters.year))
        trackEvent('scores_no_results', {
          year: activeFilters.year,
          ordering: activeOrdering,
          page: newPage,
        })
        navigate(`/vaseis/${activeFilters.year}`, { replace: true })
        return
      }
    }

    // 3) Fetch actual rows (and ALL fields for each score)
    let query = supabase
      .from('scores')
      .select(
        `id, year, score,
         departments!inner(id, name, university, city, slug),
         score_fields(fields!inner(number))`,
        { count: 'exact' }
      )
      .order('score', { ascending: activeOrdering === 'score' })

    if (activeFilters.year) query = query.eq('year', Number(activeFilters.year))
    if (deptIds?.length) query = query.in('departments.id', deptIds)
    if (activeFilters.city) query = query.eq('departments.city', activeFilters.city)
    if (activeFilters.scoreMin) query = query.gte('score', Number(activeFilters.scoreMin))
    if (activeFilters.scoreMax) query = query.lte('score', Number(activeFilters.scoreMax))

    if (activeFilters.field) {
      // Only these 15 ids -> safe IN
      query = query.in('id', pagedScoreIds)
    } else {
      // Normal pagination
      query = query.range((newPage - 1) * PAGE_SIZE, newPage * PAGE_SIZE - 1)
    }

    const { data, count, error } = await query

    if (myReqId !== requestIdRef.current) return

    if (error) {
      console.error(error)
      reportError(error, {
        where: 'scores_main_query',
        filters: activeFilters,
        ordering: activeOrdering,
        page: newPage,
      })
      setResults([])
      setTotalResults(0)
      setHasNext(false)
      setHasPrev(false)
    } else {
      const mappedResults = (data || []).map((row) => {
        const nums = row.score_fields?.map((sf) => sf.fields.number) || []
        nums.sort((a, b) => a - b)

        return {
          department: row.departments,
          year: row.year,
          score: row.score,
          field_numbers: nums,
        }
      })

      // IN() doesn't guarantee order; re-sort client-side in field mode
      if (activeFilters.field) {
        mappedResults.sort((a, b) =>
          activeOrdering === 'score' ? a.score - b.score : b.score - a.score
        )
      }

      setResults(mappedResults)

      const total = activeFilters.field ? totalCount : (count || 0)
      setTotalResults(total || 0)
      setHasNext(newPage < Math.ceil((total || 0) / PAGE_SIZE))
      setHasPrev(newPage > 1)

      trackEvent('scores_results_loaded', {
        year: activeFilters.year,
        ordering: activeOrdering,
        page: newPage,
        total: total || 0,
      })      
    }

    setLoading(false)
    setDisplayYear(Number(activeFilters.year))
    const qs = buildScoresQuery({ filters: activeFilters, ordering: activeOrdering, page: newPage })
    navigate(`/vaseis/${activeFilters.year}${qs ? `?${qs}` : ''}`, { replace: true })
  }

  useEffect(() => {
    const key = `scores:${location.pathname}:year=${selectedYear}:ordering=${ordering}`

    if (initialFetchKeys.has(key)) return
    initialFetchKeys.add(key)

    // IMPORTANT: use the correct year in filters for the fetch
    const updatedFilters = { ...filters, year: selectedYear.toString() }
    setFilters(updatedFilters)
    handleSubmit(null, 1, updatedFilters, ordering)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, ordering, location.pathname])

  return (
    <>
    <DocumentMeta
      title={`Βάσεις Πανελληνίων ${displayYear} - Όλες οι Σχολές | Πανελλήνιες+`}
      description={`Δες τις βάσεις Πανελληνίων ${displayYear} για όλα τα τμήματα. Φίλτρα ανά πεδίο, πόλη και ταξινόμηση.`}
      canonical={`https://www.panellhniesplus.gr${location.pathname}`}
    />
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Αρχική", item: "https://www.panellhniesplus.gr/" },
        { "@type": "ListItem", position: 2, name: "Βάσεις", item: "https://www.panellhniesplus.gr/vaseis" },
        { "@type": "ListItem", position: 3, name: `Βάσεις ${displayYear}`, item: `https://www.panellhniesplus.gr/vaseis/${displayYear}` },
      ],
    }) }} />
    <div className="min-h-screen bg-white text-gray-900">
      <Header />

      {/* Mobile controls bar */}
      <div className="md:hidden sticky top-16 z-30 bg-white/95 backdrop-blur border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between">
          <button
            type="button"
            onClick={() => {
              trackEvent('scores_filters_opened', { mode: 'mobile' })
              setShowFilters(true)
            }}            
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-semibold text-gray-800 hover:bg-gray-50 cursor-pointer"
          >
            Φίλτρα
          </button>

          <div className="flex items-center gap-2">
            <label htmlFor="ordering-m" className="text-sm font-medium text-gray-700">
              Ταξινόμηση
            </label>
            <select
              id="ordering-m"
              value={ordering}
              onChange={(e) => {
                const newOrdering = e.target.value
                trackEvent('scores_ordering_changed', { ordering: newOrdering })
                setOrdering(newOrdering)
                handleSubmit(null, 1, null, newOrdering)
              }}
              className="cursor-pointer border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium text-gray-800 focus:ring-2 focus:ring-gray-500 focus:outline-none bg-white"
            >
              <option value="-score">Φθίνουσα</option>
              <option value="score">Αύξουσα</option>
            </select>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Filters Sidebar (desktop only) */}
          <aside className="hidden md:block md:w-[320px]">
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="px-6 py-5 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900">Φίλτρα</h2>
                <p className="text-sm text-gray-500 mt-1">Προσαρμόστε την αναζήτηση σας</p>
              </div>

              <form className="p-6 space-y-5" onSubmit={(e) => handleSubmit(e, 1)}>
                {/* Year */}
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Έτος</label>
                  <select
                    name="year"
                    value={filters.year}
                    onChange={handleChange}
                    className="w-full cursor-pointer border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-900 h-11 focus:ring-2 focus:ring-gray-500 focus:outline-none bg-white"
                  >
                    {Array.from({ length: MAX_YEAR - MIN_YEAR + 1 }, (_, i) => {
                      const y = MAX_YEAR - i
                      return (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      )
                    })}
                  </select>
                </div>

                {/* Field */}
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Πεδίο</label>
                  <select
                    name="field"
                    value={filters.field}
                    onChange={handleChange}
                    className="w-full cursor-pointer border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-900 h-11 focus:ring-2 focus:ring-gray-500 focus:outline-none bg-white"
                  >
                    <option value="">Όλα</option>
                    {fields.map((f) => (
                      <option key={f.id} value={f.id}>
                        Πεδίο {f.number}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Department */}
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Τμήμα</label>
                  <input
                    name="department"
                    value={filters.department}
                    onChange={handleChange}
                    placeholder="π.χ. Πληροφορική"
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-900 h-11 focus:ring-2 focus:ring-gray-500 focus:outline-none placeholder:text-gray-400 bg-white"
                  />
                </div>

                {/* City */}
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Πόλη</label>
                  <select
                    name="city"
                    value={filters.city}
                    onChange={handleChange}
                    className="w-full cursor-pointer border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-900 h-11 focus:ring-2 focus:ring-gray-500 focus:outline-none bg-white"
                  >
                    <option value="">Όλες</option>
                    {sortedCities.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Score */}
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Μόρια</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-1">Ελάχιστο</div>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        name="scoreMin"
                        value={filters.scoreMin}
                        onChange={handleChange}
                        placeholder="12000"
                        className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-900 h-11 focus:ring-2 focus:ring-gray-500 focus:outline-none placeholder:text-gray-400 bg-white"
                      />
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-1">Μέγιστο</div>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        name="scoreMax"
                        value={filters.scoreMax}
                        onChange={handleChange}
                        placeholder="18000"
                        className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-900 h-11 focus:ring-2 focus:ring-gray-500 focus:outline-none placeholder:text-gray-400 bg-white"
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full cursor-pointer bg-gray-900 text-white py-3 rounded-xl font-semibold hover:bg-gray-800 transition"
                >
                  Αναζήτηση
                </button>
              </form>
            </div>
          </aside>

          {/* Results Section */}
          <main className="flex-1 min-w-0">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between mb-6">
              <div>
                <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900">
                  Βάσεις Πανελληνίων {displayYear}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  Αναζήτησε τμήματα και δες τα μόρια προηγούμενων ετών
                </p>
              </div>

              {/* Desktop ordering + count */}
              <div className="hidden md:flex items-center gap-4">
                <p className="text-sm text-gray-600">
                  Βρέθηκαν <span className="font-semibold text-gray-900">{totalResults}</span>
                </p>
                <div className="flex items-center gap-2">
                  <label htmlFor="ordering" className="text-sm font-semibold text-gray-800">
                    Ταξινόμηση
                  </label>
                  <select
                    id="ordering"
                    value={ordering}
                    onChange={(e) => {
                      const newOrdering = e.target.value
                      setOrdering(newOrdering)
                      handleSubmit(null, 1, null, newOrdering)
                    }}
                    className="border border-gray-300 rounded-xl px-3 py-2 text-sm font-medium text-gray-900 h-11 focus:ring-2 focus:ring-gray-500 focus:outline-none cursor-pointer bg-white"
                  >
                    <option value="-score">Φθίνουσα</option>
                    <option value="score">Αύξουσα</option>
                  </select>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-9 w-9 border-t-2 border-b-2 border-gray-500"></div>
              </div>
            ) : results.length > 0 ? (
              <>
                {/* Mobile count */}
                <div className="md:hidden mb-4 text-sm text-gray-600">
                  Βρέθηκαν <span className="font-semibold text-gray-900">{totalResults}</span> αποτελέσματα
                </div>

                <ul className="space-y-3">
                  {results.map((item, index) => (
                    <li
                      key={index}
                      className="bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => {
                        trackEvent('scores_department_opened', {
                          department_id: item.department.id,
                          year: item.year,
                        })
                        navigate(`/tmima/${item.department.slug}`, {
                          state: { from: `${location.pathname}${location.search}` },
                        })
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          trackEvent('scores_department_opened', {
                            department_id: item.department.id,
                            year: item.year,
                          })
                          navigate(`/tmima/${item.department.slug}`, {
                            state: { from: `${location.pathname}${location.search}` },
                          })
                        }
                      }}
                    >
                      <div className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="min-w-0">
                          <p className="font-bold text-gray-900 leading-snug">
                            {item.department.name}{' '}
                            <span className="font-semibold text-gray-700">• {item.department.university}</span>
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            {item.department.city} • {item.year}
                          </p>

                          {item.field_numbers.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {item.field_numbers.map((n) => (
                                <span
                                  key={n}
                                  className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 border border-gray-200"
                                >
                                  Πεδίο {n}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="shrink-0">
                          {item.score > 0 ? (
                            <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-center">
                              <div className="text-xs font-semibold text-gray-500">Μόρια</div>
                              <div className="text-xl font-extrabold text-gray-900 leading-none mt-1">
                                {item.score}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="mt-10 flex flex-col sm:flex-row sm:flex-wrap justify-center items-center gap-2">
                  <button
                    onClick={() => handleSubmit(null, 1)}
                    disabled={page === 1}
                    className={`hidden sm:inline-flex items-center gap-1 px-4 py-2.5 rounded-xl border text-sm font-semibold transition ${
                      page === 1
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50 cursor-pointer'
                    }`}
                  >
                    <ChevronDoubleLeftIcon className="w-4 h-4" /> Πρώτη
                  </button>

                  <button
                    onClick={() => hasPrev && handleSubmit(null, page - 1)}
                    disabled={!hasPrev}
                    className={`flex items-center gap-1 px-4 py-2.5 rounded-xl border text-sm font-semibold transition ${
                      !hasPrev
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50 cursor-pointer'
                    }`}
                  >
                    <ChevronLeftIcon className="w-4 h-4" /> Προηγούμενη
                  </button>

                  <span className="px-4 py-2.5 rounded-xl text-sm font-bold text-gray-800 bg-gray-50 border border-gray-200">
                    Σελίδα {page}
                  </span>

                  <button
                    onClick={() => hasNext && handleSubmit(null, page + 1)}
                    disabled={!hasNext}
                    className={`flex items-center gap-1 px-4 py-2.5 rounded-xl border text-sm font-semibold transition ${
                      !hasNext
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50 cursor-pointer'
                    }`}
                  >
                    Επόμενη <ChevronRightIcon className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => handleSubmit(null, lastPage || 1)}
                    disabled={page >= (lastPage || 1)}
                    className={`hidden sm:inline-flex items-center gap-1 px-4 py-2.5 rounded-xl border text-sm font-semibold transition ${
                      page >= (lastPage || 1)
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50 cursor-pointer'
                    }`}
                  >
                    Τελευταία <ChevronDoubleRightIcon className="w-4 h-4" />
                  </button>
                </div>

                {/* In-content ad — below the results list */}
                <AdUnit />
              </>
            ) : (
              <div className="text-center text-gray-500 py-16">
                <div className="mx-auto max-w-md border border-gray-200 rounded-2xl bg-gray-50 p-6">
                  <p className="font-semibold text-gray-900">Δεν βρέθηκαν αποτελέσματα</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Δοκίμασε να αλλάξεις φίλτρα (π.χ. πόλη ή εύρος μορίων).
                  </p>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Mobile Filters Sheet */}
      {showFilters && (
        <div className="fixed inset-0 z-[1000] md:hidden">
          <div
            className="absolute inset-0 bg-black/50 cursor-pointer"
            onClick={() => setShowFilters(false)}
            aria-hidden="true"
          />
          <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-3xl shadow-2xl border-t border-gray-200 max-h-[85vh] flex flex-col animate-[sheetIn_.18s_ease-out]">
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <div className="flex-1 flex justify-center">
                <span className="block h-1.5 w-12 bg-gray-300 rounded-full" />
              </div>
            </div>

            <div className="overflow-y-auto px-4 pb-4 flex-1">
              <div className="pb-2">
                <h3 className="text-base font-bold text-gray-900">Φίλτρα</h3>
                <p className="text-sm text-gray-500 mt-0.5">Εφάρμοσε φίλτρα και πάτα αναζήτηση</p>
              </div>

              <form
                className="space-y-5"
                onSubmit={(e) => {
                  handleSubmit(e, 1)
                  setShowFilters(false)
                }}
              >
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Έτος</label>
                  <select
                    name="year"
                    value={filters.year}
                    onChange={handleChange}
                    className="w-full cursor-pointer border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-900 h-11 focus:ring-2 focus:ring-gray-500 focus:outline-none bg-white"
                  >
                    {Array.from({ length: MAX_YEAR - MIN_YEAR + 1 }, (_, i) => {
                      const y = MAX_YEAR - i
                      return (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      )
                    })}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Πεδίο</label>
                  <select
                    name="field"
                    value={filters.field}
                    onChange={handleChange}
                    className="w-full cursor-pointer border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-900 h-11 focus:ring-2 focus:ring-gray-500 focus:outline-none bg-white"
                  >
                    <option value="">Όλα</option>
                    {fields.map((f) => (
                      <option key={f.id} value={f.id}>
                        Πεδίο {f.number}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Τμήμα</label>
                  <input
                    name="department"
                    value={filters.department}
                    onChange={handleChange}
                    placeholder="π.χ. Πληροφορική"
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-900 h-11 focus:ring-2 focus:ring-gray-500 focus:outline-none placeholder:text-gray-400 bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Πόλη</label>
                  <select
                    name="city"
                    value={filters.city}
                    onChange={handleChange}
                    className="w-full cursor-pointer border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-900 h-11 focus:ring-2 focus:ring-gray-500 focus:outline-none bg-white"
                  >
                    <option value="">Όλες</option>
                    {GREEK_UNI_CITIES.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Μόρια</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-1">Ελάχιστο</div>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        name="scoreMin"
                        value={filters.scoreMin}
                        onChange={handleChange}
                        placeholder="12000"
                        className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-900 h-11 focus:ring-2 focus:ring-gray-500 focus:outline-none placeholder:text-gray-400 bg-white"
                      />
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-1">Μέγιστο</div>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        name="scoreMax"
                        value={filters.scoreMax}
                        onChange={handleChange}
                        placeholder="18000"
                        className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-900 h-11 focus:ring-2 focus:ring-gray-500 focus:outline-none placeholder:text-gray-400 bg-white"
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full cursor-pointer bg-gray-900 text-white py-3 rounded-xl font-semibold hover:bg-gray-800 transition"
                >
                  Αναζήτηση
                </button>

                <button
                  type="button"
                  onClick={() => setShowFilters(false)}
                  className="w-full cursor-pointer bg-white text-gray-900 py-3 rounded-xl font-semibold border border-gray-300 hover:bg-gray-50 transition"
                >
                  Κλείσιμο
                </button>
              </form>
            </div>
          </div>

          <style>{`
            @keyframes sheetIn {
              from { transform: translateY(12%); opacity: .7; }
              to   { transform: translateY(0);   opacity: 1;  }
            }
            @media (prefers-reduced-motion: reduce) {
              .animate-[sheetIn_.18s_ease-out] { animation: none; }
            }
          `}</style>
        </div>
      )}

      <Footer />
    </div>
    </>
  )
}

export default ScoresPage
